<script setup>

import {useConfigurateurStore} from "../../stores/configurateur";
import {onMounted, nextTick, ref, watch, computed} from "vue";

const store = useConfigurateurStore();

const galleryWrapper = ref(null);
const galleryKey = ref(0);
const currentSlideIndex = ref(1);
const swiperInstance = ref(null);

const nextSlide = () => {
    if (swiperInstance.value) {
        try {
            swiperInstance.value.slideNext();
        } catch (error) {
            // Silently handle error
        }
    }
};

const prevSlide = () => {
    if (swiperInstance.value) {
        try {
            swiperInstance.value.slidePrev();
        } catch (error) {
            // Silently handle error
        }
    }
};

const initSwiperGallery = () => {
    if (galleryWrapper.value && window.initSwiperGalleryWithThumbs) {
        try {
            window.initSwiperGalleryWithThumbs(galleryWrapper.value);

            const gallerySwiper = galleryWrapper.value.querySelector('.swiper.is-gallery');

            if (gallerySwiper && gallerySwiper.swiper) {
                swiperInstance.value = gallerySwiper.swiper;

                swiperInstance.value.on('slideChange', function () {
                    currentSlideIndex.value = this.activeIndex + 1;
                });
                swiperInstance.value.on('init', function () {
                    currentSlideIndex.value = this.activeIndex + 1;
                });

                currentSlideIndex.value = swiperInstance.value.activeIndex + 1;
            } else {
                initCustomSwiper();
            }
        } catch (error) {
            initCustomSwiper();
        }
    } else {
        initCustomSwiper();
    }
};

const initCustomSwiper = () => {
    if (!galleryWrapper.value) return;

    const SwiperClass = window.Swiper || window.swiper || (window.Swiper && window.Swiper.default) ||
        (typeof Swiper !== 'undefined' ? Swiper : null);

    if (!SwiperClass) {
        setTimeout(() => {
            try {
                if (window.initSwiperGalleryWithThumbs) {
                    window.initSwiperGalleryWithThumbs(galleryWrapper.value);

                    setTimeout(() => {
                        const gallerySwiper = galleryWrapper.value.querySelector('.swiper.is-gallery');
                        if (gallerySwiper && gallerySwiper.swiper) {
                            swiperInstance.value = gallerySwiper.swiper;
                            currentSlideIndex.value = swiperInstance.value.activeIndex + 1;
                        } else {
                            useManualNavigation();
                        }
                    }, 500);
                }
            } catch (error) {
                useManualNavigation();
            }
        }, 300);

        return;
    }

    const gallerySwiper = galleryWrapper.value.querySelector('.swiper.is-gallery');
    if (!gallerySwiper) return;

    if (gallerySwiper.swiper) {
        gallerySwiper.swiper.destroy(true, true);
    }

    setTimeout(() => {
        try {
            swiperInstance.value = new SwiperClass(gallerySwiper, {
                slidesPerView: 1,
                spaceBetween: 0,
                loop: false,
                navigation: {
                    nextEl: '.custom-nav-next',
                    prevEl: '.custom-nav-prev',
                },
                pagination: {
                    el: '.swiper-pagination',
                    clickable: true,
                    dynamicBullets: true,
                },
                keyboard: {enabled: true},
                mousewheel: false,
                effect: 'slide',
                speed: 300,
                allowTouchMove: true,
                on: {
                    slideChange: function () {
                        currentSlideIndex.value = this.activeIndex + 1;
                    },
                    init: function () {
                        currentSlideIndex.value = this.activeIndex + 1;
                    }
                }
            });
        } catch (error) {
            useManualNavigation();
        }
    }, 200);
};

const useManualNavigation = () => {
    const gallerySwiper = galleryWrapper.value?.querySelector('.swiper.is-gallery');
    if (gallerySwiper) {
        const slides = gallerySwiper.querySelectorAll('.swiper-slide');
        let currentIndex = 0;

        swiperInstance.value = {
            slides: slides,
            activeIndex: currentIndex,
            slideNext: () => {
                if (currentIndex < slides.length - 1) {
                    currentIndex++;
                    updateSlidePosition();
                }
            },
            slidePrev: () => {
                if (currentIndex > 0) {
                    currentIndex--;
                    updateSlidePosition();
                }
            }
        };

        const updateSlidePosition = () => {
            const wrapper = gallerySwiper.querySelector('.swiper-wrapper');
            if (wrapper) {
                wrapper.style.transform = `translate3d(-${currentIndex * 100}%, 0px, 0px)`;
                wrapper.style.transition = 'transform 300ms';
                swiperInstance.value.activeIndex = currentIndex;
                currentSlideIndex.value = currentIndex + 1;
            }
        };

        updateSlidePosition();
    }
};

onMounted(() => {
    // L'initialisation au montage se fait sur le DOM initial
    nextTick(() => {
        initSwiperGallery();
    });
});

watch(() => store.images, () => {
    nextTick(() => {
        // Réinitialiser le swiper après changement des images
        initSwiperGallery();
    });
}, {deep: true});

// Watch pour réinitialiser quand displayVideo change
watch(() => store.displayVideo, () => {
    nextTick(() => {
        initSwiperGallery();
    });
});

const totalSlides = computed(() => {
    let count = store.images.length;
    if (store.displayVideo) count++;
    return count;
});

// Computed pour déterminer quelles informations afficher
const shouldShowInfo = computed(() => {
    // Masquer après l'étape 2
    if (isFromStep3.value) {
        return false;
    }
    return (store.voiture_id && showVoitureInfo.value) || (store.circuit_id && showCircuitInfo.value);
});

const showVoitureInfo = computed(() => {
    return store.step === 'voiture' || (store.step === 'circuit' && !store.circuit_id) || (store.step !== 'voiture' && store.step !== 'circuit' && store.voiture_id);
});

const showCircuitInfo = computed(() => {
    return store.step === 'circuit' || (store.step === 'voiture' && !store.voiture_id) || (store.step !== 'voiture' && store.step !== 'circuit' && store.circuit_id && !showVoitureInfo.value);
});

// Computed pour déterminer si on est à partir de l'étape 3
const isFromStep3 = computed(() => {
    const currentStepIndex = store.steps.indexOf(store.step);
    return currentStepIndex >= 2; // À partir de l'étape 3 (index 2)
});

// Images pour la vue divisée
const circuitImage = computed(() => {
    /*if (store.circuit && store.circuit.images && store.circuit.images.length > 0) {
        return store.circuit.images[0].original_url;
    }*/
    return store.circuit?.image || null;
});

const voitureImage = computed(() => {
    /*if (store.voiture && store.voiture.images && store.voiture.images.length > 0) {
        // Chercher d'abord une image de profil, sinon prendre la première
        const profilImage = store.voiture.images.find(img => img.collection_name === 'profil');
        return profilImage ? profilImage.original_url : store.voiture.images[0].original_url;
    }*/
    return store.voiture?.image_principale || null;
});


</script>

<template>
    <div ref="galleryWrapper" class="gallery position-relative" :key="galleryKey">
        <!-- Vue normale (étapes 1 et 2) -->
        <div v-if="!isFromStep3" class="swiper is-gallery">
            <!-- Icônes en superposition pour les étapes complétées -->
            <div class="step-icons-overlay" v-if="store.completedStepsIcons.length > 0" style="display:none;">
                <div
                    v-for="(stepIcon, index) in store.completedStepsIcons"
                    :key="stepIcon.step"
                    class="step-icon"
                    :style="{ 
                        bottom: `${20 + (index * 40)}px`,
                        right: '10px'
                    }"
                    :title="$t(stepIcon.step)"
                >
                    <i :class="stepIcon.icon" class=""></i>
                </div>
            </div>

            <div class="swiper-wrapper">
                <template v-if="store.displayVideo">
                    <div class="swiper-slide video-slide">
                        <div v-safe-html:embed="store.voitureCircuit.video"></div>
                    </div>
                </template>

                <div class="swiper-slide" v-for="(image, index) in store.images" :key="image.original_url || index">
                    <img :class="`img-fluid rounded ${image.collection_name}`" :src="image.original_url" alt="">
                </div>
            </div>

            <!-- Contrôles de navigation personnalisés -->
            <button
                class="custom-nav-button custom-nav-next"
                @click="nextSlide"
                :disabled="currentSlideIndex >= totalSlides"
                type="button"
                style="pointer-events: all;">
                <i class="bi bi-chevron-right"></i>
            </button>
            <button
                class="custom-nav-button custom-nav-prev"
                @click="prevSlide"
                :disabled="currentSlideIndex <= 1"
                type="button"
                style="pointer-events: all;">
                <i class="bi bi-chevron-left"></i>
            </button>

            <!-- Pagination -->
            <div class="swiper-pagination"></div>

            <!-- Compteur de slides (optionnel) -->
            <div class="slide-counter" v-if="totalSlides > 1">
                <span class="current-slide">{{ currentSlideIndex }}</span> / <span class="total-slides">{{
                    totalSlides
                }}</span>
            </div>
        </div>

        <!-- Vue divisée (à partir de l'étape 3) -->
        <div v-else class="gallery-split-view">
            <!-- Photo du circuit à gauche -->
            <div class="split-left me-3">
                <div class="split-image-container">
                    <img v-if="circuitImage" :src="circuitImage" alt="Circuit" class="split-image">
                    <div v-else class="split-placeholder">
                        <i class="bi bi-geo-alt fs-1 text-muted"></i>
                        <p class="text-muted mt-2">{{ $t('Image du circuit') }}</p>
                    </div>
                    <div class="split-label">
                        <span class="badge bg-primary">{{ $t('Circuit') }}</span>
                        <h5 class="mt-2 text-white fw-bold" v-if="store.circuit.designation">
                            {{ store.circuit.designation + (store.circuit.piste ? ` - ${store.circuit.piste}` : '') }}</h5>
                    </div>
                </div>

                <div class="gallery-info-section mt-3">
                <div class="card-circuit-infos">
                <div class="info-header mb-3">
                    <h4 class="h5 fw-bold text-primary mb-0">
                        {{ store.circuit.designation + (store.circuit.piste ? ` - ${store.circuit.piste}` : '') }}
                    </h4>
                </div>
                <div class="card-text">
                    <div class="d-flex justify-content-between text-gray-70">
                        <div class="d-block d-md-flex text-center text-md-start">
                            <i class="bi bi-speedometer fs-4 me-2 text-primary"></i>
                            <div>
                                <p class="fw-bold small lh-sm mb-0">Vitesse</p>
                                <p class="fw-bold small lh-sm mb-0">{{ store.circuit.info_vitesse }}/10</p>
                            </div>
                        </div>

                        <div class="d-block d-md-flex text-center text-md-start">
                            <i class="bi bi-segmented-nav fs-4 me-2 text-primary"></i>
                            <div>
                                <p class="fw-bold small lh-sm mb-0">Technique</p>
                                <p class="fw-bold small lh-sm mb-0">{{ store.circuit.info_technique }}/10</p>
                            </div>
                        </div>

                        <div class="d-block d-md-flex text-center text-md-start">
                            <i class="bi bi-joystick fs-4 me-2 text-primary"></i>
                            <div>
                                <p class="fw-bold small lh-sm mb-0">Longueur</p>
                                <p class="fw-bold small lh-sm mb-0">{{ store.circuit.info_longueur }}KM</p>
                            </div>
                        </div>
                    </div>
                </div>
                </div>
            </div>

            </div>

            <!-- Photo de la voiture à droite -->
            <div class="split-right">
                <div class="split-image-container">
                    <img v-if="voitureImage" :src="voitureImage" alt="Voiture" class="split-image">
                    <div v-else class="split-placeholder">
                        <i class="bi bi-car-front fs-1 text-muted"></i>
                        <p class="text-muted mt-2">{{ $t('Image de la voiture') }}</p>
                    </div>
                    <div class="split-label">
                        <span class="badge bg-secondary">{{ $t('Voiture') }}</span>
                        <h5 class="mt-2 text-white fw-bold" v-if="store.voiture">{{ store.voiture.marque }} {{
                                store.voiture.designation
                            }}</h5>
                    </div>
                </div>
                
                <div class="gallery-info-section mt-3">
                <div class="card-voiture-infos">
                    <div class="info-header mb-3">
                        <h4 class="h5 fw-bold text-primary mb-0">
                            {{ store.voiture.marque }} {{ store.voiture.designation }}
                        </h4>
                    </div>
                    <div class="card-text">
                        <div class="d-flex justify-content-between text-gray-70">
                            <div class="d-block d-md-flex text-center text-md-start">
                                <i class="bi bi-speedometer fs-4 me-2 text-primary"></i>
                                <div>
                                    <p class="fw-bold small lh-sm mb-0">{{ store.voiture.vitesse_max }}</p>
                                    <p class="fw-bold small lh-sm mb-0">{{ store.voiture.acceleration }}/10</p>
                                </div>
                            </div>

                            <div class="d-block d-md-flex text-center text-md-start">
                                <i class="bi bi-segmented-nav fs-4 me-2 text-primary"></i>
                                <div>
                                    <p class="fw-bold small lh-sm mb-0">{{ store.voiture.puissance }}</p>
                                    <p class="fw-bold small lh-sm mb-0">{{ store.voiture.poids_puissance }}/10</p>
                                </div>
                            </div>

                            <div class="d-block d-md-flex text-center text-md-start">
                                <svg style="fill: #0c4c91!important;margin-right:5px;" class='cylindree' xmlns="http://www.w3.org/2000/svg" shape-rendering="geometricPrecision" text-rendering="geometricPrecision" image-rendering="optimizeQuality" fill-rule="evenodd" clip-rule="evenodd" viewBox="0 0 512 509.77" ><path d="M244.48 209.89c1.8 18.05-22.84 51.63-33.52 62.51-21.3 21.66-40.94 45.37-73.68 69.79-23.43 17.47-29.49 19.61-57.67 19.22l-1.02-.05c-10.72 0-21.43 2.45-31.23 7.36l-14.85-14.85c-1.98-1.98-5.21-1.98-7.18 0L1.48 377.7c-1.97 1.99-1.97 5.22 0 7.2l14.79 14.78a69.844 69.844 0 0 0-7.46 30.47l-.04 1.04c0 17.86 6.81 35.74 20.44 49.36 13.58 13.58 31.43 20.39 49.35 20.42h.06c10.81 0 21.61-2.5 31.47-7.47l14.78 14.79c1.98 1.97 5.21 1.97 7.19 0l23.84-23.85c1.98-1.97 1.98-5.19 0-7.18l-14.85-14.85c4.91-9.8 7.36-20.51 7.36-31.23 0-29.62 1.48-35.84 20.2-60.62 24.44-32.39 47.49-50.85 68.75-71.76 10.86-10.68 44.46-33.54 62.5-33.53l-55.38-55.38zM355.29.95c70.35 38.85 119.62 87.62 155.76 155.77 1.01 1.87 1.5 3.96 0 5.46l-17.61 17.61L332.22 18.56 349.83.95c1.5-1.5 3.61-1.01 5.46 0zm11.67 144.1c12.77 12.78 12.77 33.49-.01 46.27-12.77 12.77-33.48 12.78-46.26 0-12.78-12.78-12.78-33.5 0-46.27 12.78-12.78 33.49-12.78 46.27 0zm114.96 46.27-10.68 10.67L310.01 40.77l10.68-10.68 161.23 161.23zm-22.21 22.2L357.59 315.65c-1.47 1.46-3.98 1.46-5.46-.01l-24.44-24.45 5.73-5.74c13.43-13.43.19-17.84-13.19-31.23l-62.43-62.43c-13.39-13.38-17.86-26.58-31.24-13.2l-5.74 5.74-24.46-24.45c-1.47-1.48-1.5-3.96 0-5.46L298.48 52.29l161.23 161.23zm-354.15 217.6c0 14.68-11.9 26.59-26.59 26.59-14.68 0-26.58-11.91-26.58-26.59 0-14.69 11.9-26.59 26.58-26.59 14.69 0 26.59 11.9 26.59 26.59z"></path></svg>

                                <div>
                                    <p class="fw-bold small lh-sm mb-0">{{ store.voiture.cyclindree }}</p>
                                    <p class="fw-bold small lh-sm mb-0">{{ store.voiture.prise_en_main }}/10</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                </div>

            </div>

            <!-- Icônes en superposition pour les étapes complétées -->
            <div class="step-icons-overlay" v-if="store.completedStepsIcons.length > 0" style="display:none;">
                <div
                    v-for="(stepIcon, index) in store.completedStepsIcons"
                    :key="stepIcon.step"
                    class="step-icon"
                    :style="{ 
                        bottom: `${20 + (index * 40)}px`,
                        right: '20px'
                    }"
                    :title="$t(stepIcon.step)"
                >
                    <i :class="stepIcon.icon" class=""></i>
                </div>
            </div>
        </div>

        <!-- Informations de la voiture/circuit sélectionné -->
        <div class="gallery-info-section mt-3" v-if="shouldShowInfo">
            <!-- Informations de la voiture -->
            <div v-if="showVoitureInfo && store.voiture" class="card-voiture-infos">
                <div class="info-header mb-3">
                    <h4 class="h5 fw-bold text-primary mb-0">
                        {{ store.voiture.marque }} {{ store.voiture.designation }}
                    </h4>
                </div>
                <div class="card-text">
                    <div class="d-flex justify-content-between text-gray-70">
                        <div class="d-block d-md-flex text-center text-md-start">
                            <i class="bi bi-speedometer fs-4 me-2 text-primary"></i>
                            <div>
                                <p class="fw-bold small lh-sm mb-0">{{ store.voiture.vitesse_max }}</p>
                                <p class="fw-bold small lh-sm mb-0">{{ store.voiture.acceleration }}/10</p>
                            </div>
                        </div>

                        <div class="d-block d-md-flex text-center text-md-start">
                            <i class="bi bi-segmented-nav fs-4 me-2 text-primary"></i>
                            <div>
                                <p class="fw-bold small lh-sm mb-0">{{ store.voiture.puissance }}</p>
                                <p class="fw-bold small lh-sm mb-0">{{ store.voiture.poids_puissance }}/10</p>
                            </div>
                        </div>

                        <div class="d-block d-md-flex text-center text-md-start">
                            <svg style="fill: #0c4c91!important;margin-right:5px;" class='cylindree' xmlns="http://www.w3.org/2000/svg" shape-rendering="geometricPrecision" text-rendering="geometricPrecision" image-rendering="optimizeQuality" fill-rule="evenodd" clip-rule="evenodd" viewBox="0 0 512 509.77" ><path d="M244.48 209.89c1.8 18.05-22.84 51.63-33.52 62.51-21.3 21.66-40.94 45.37-73.68 69.79-23.43 17.47-29.49 19.61-57.67 19.22l-1.02-.05c-10.72 0-21.43 2.45-31.23 7.36l-14.85-14.85c-1.98-1.98-5.21-1.98-7.18 0L1.48 377.7c-1.97 1.99-1.97 5.22 0 7.2l14.79 14.78a69.844 69.844 0 0 0-7.46 30.47l-.04 1.04c0 17.86 6.81 35.74 20.44 49.36 13.58 13.58 31.43 20.39 49.35 20.42h.06c10.81 0 21.61-2.5 31.47-7.47l14.78 14.79c1.98 1.97 5.21 1.97 7.19 0l23.84-23.85c1.98-1.97 1.98-5.19 0-7.18l-14.85-14.85c4.91-9.8 7.36-20.51 7.36-31.23 0-29.62 1.48-35.84 20.2-60.62 24.44-32.39 47.49-50.85 68.75-71.76 10.86-10.68 44.46-33.54 62.5-33.53l-55.38-55.38zM355.29.95c70.35 38.85 119.62 87.62 155.76 155.77 1.01 1.87 1.5 3.96 0 5.46l-17.61 17.61L332.22 18.56 349.83.95c1.5-1.5 3.61-1.01 5.46 0zm11.67 144.1c12.77 12.78 12.77 33.49-.01 46.27-12.77 12.77-33.48 12.78-46.26 0-12.78-12.78-12.78-33.5 0-46.27 12.78-12.78 33.49-12.78 46.27 0zm114.96 46.27-10.68 10.67L310.01 40.77l10.68-10.68 161.23 161.23zm-22.21 22.2L357.59 315.65c-1.47 1.46-3.98 1.46-5.46-.01l-24.44-24.45 5.73-5.74c13.43-13.43.19-17.84-13.19-31.23l-62.43-62.43c-13.39-13.38-17.86-26.58-31.24-13.2l-5.74 5.74-24.46-24.45c-1.47-1.48-1.5-3.96 0-5.46L298.48 52.29l161.23 161.23zm-354.15 217.6c0 14.68-11.9 26.59-26.59 26.59-14.68 0-26.58-11.91-26.58-26.59 0-14.69 11.9-26.59 26.58-26.59 14.69 0 26.59 11.9 26.59 26.59z"></path></svg>

                            <div>
                                <p class="fw-bold small lh-sm mb-0">{{ store.voiture.cyclindree }}</p>
                                <p class="fw-bold small lh-sm mb-0">{{ store.voiture.prise_en_main }}/10</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Informations du circuit -->
            <div v-if="showCircuitInfo && store.circuit" class="card-circuit-infos">
                <div class="info-header mb-3">
                    <h4 class="h5 fw-bold text-primary mb-0">
                        {{ store.circuit.designation + (store.circuit.piste ? ` - ${store.circuit.piste}` : '') }}
                    </h4>
                </div>
                <div class="card-text">
                    <div class="d-flex justify-content-between text-gray-70">
                        <div class="d-block d-md-flex text-center text-md-start">
                            <i class="bi bi-speedometer fs-4 me-2 text-primary"></i>
                            <div>
                                <p class="fw-bold small lh-sm mb-0">Vitesse</p>
                                <p class="fw-bold small lh-sm mb-0">{{ store.circuit.info_vitesse }}/10</p>
                            </div>
                        </div>

                        <div class="d-block d-md-flex text-center text-md-start">
                            <i class="bi bi-segmented-nav fs-4 me-2 text-primary"></i>
                            <div>
                                <p class="fw-bold small lh-sm mb-0">Technique</p>
                                <p class="fw-bold small lh-sm mb-0">{{ store.circuit.info_technique }}/10</p>
                            </div>
                        </div>

                        <div class="d-block d-md-flex text-center text-md-start">
                            <i class="bi bi-joystick fs-4 me-2 text-primary"></i>
                            <div>
                                <p class="fw-bold small lh-sm mb-0">Longueur</p>
                                <p class="fw-bold small lh-sm mb-0">{{ store.circuit.info_longueur }}KM</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.step-icons-overlay {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    pointer-events: none;
    z-index: 10;
}

.step-icon {
    position: absolute;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.step-icon i {
    font-size: 1.2rem;
    color: #0c5dd3;
    cursor: pointer;
    transition: all .2s;
    z-index: 10;
    padding: 0 5px;
    border-radius: 20%;
    background: rgba(255, 255, 255, .9);
    box-shadow: 0 2px 4px #0000001a;
    filter: drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.5));
}

.step-icon:hover {
    background-color: rgba(0, 0, 0, 0.9);
    transform: scale(1.1);
}

.slide-counter {
    position: absolute;
    bottom: 20px;
    left: 10px;
    background: rgba(0, 0, 0, 0.7);
    color: white;
    padding: 5px 10px;
    border-radius: 15px;
    font-size: 0.9rem;
    z-index: 10;
}

.video-slide {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 523px;
}

.video-slide div {
    width: 100%;
    height: 100%;
    position: relative;
}

.video-slide iframe,
.video-slide video {
    width: 100%;
    height: 100%;
}

@media (max-width: 992px) and (min-width: 768px) {
    .video-slide {
        height: 261px;
    }
}

/* Boutons de navigation personnalisés */
.custom-nav-button {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.9);
    border: none;
    color: #0c5dd3;
    font-size: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    z-index: 10;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.custom-nav-button:hover:not(:disabled) {
    background: rgba(255, 255, 255, 1);
    transform: translateY(-50%) scale(1.1);
}

.custom-nav-button:disabled {
    opacity: 0.3;
    cursor: not-allowed;
}

.custom-nav-next {
    right: 10px;
}

.custom-nav-prev {
    left: 10px;
}

/* Personnalisation de la pagination */
.swiper-pagination-bullet {
    background: #0c5dd3;
    opacity: 0.4;
}

.swiper-pagination-bullet-active {
    opacity: 1;
}

/* Styles pour la section d'informations */
.gallery-info-section {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border: 1px solid rgba(12, 93, 211, 0.1);
}

.info-header {
    border-bottom: 2px solid #0c5dd3;
    padding-bottom: 10px;
}

.card-voiture-infos,
.card-circuit-infos {
    width: 100%;
}

.card-voiture-infos .d-flex > div,
.card-circuit-infos .d-flex > div {
    flex: 1;
    min-width: 0;
}

/* Styles pour la vue divisée */
.gallery-split-view {
    display: flex;
    height: 523px;
    border-radius: 8px;
    /*overflow: hidden;*/
    position: relative;
}

.split-left,
.split-right {
    flex: 1;
    position: relative;
    border-radius: 10px;
}

.split-image-container {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.split-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 10px;
}

.split-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    background: #f8f9fa;
}

.split-label {
    position: absolute;
    bottom: 15px;
    left: 15px;
    z-index: 5;
}

.split-label .badge {
    font-size: 0.75rem;
}

.split-label h5 {
    margin: 0;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.7);
    font-size: 1rem;
}

@media (max-width: 768px) {
    .gallery-info-section {
        padding: 15px;
    }

    .card-voiture-infos .d-flex,
    .card-circuit-infos .d-flex {
        flex-direction: column;
        gap: 15px;
    }

    .card-voiture-infos .d-flex > div,
    .card-circuit-infos .d-flex > div {
        text-align: center !important;
    }

    .gallery-split-view {
        flex-direction: column;
        height: 300px;
    }

    .split-left {
        border-right: none;
        border-bottom: 2px solid #0c5dd3;
    }

    .split-label h5 {
        font-size: 0.9rem;
    }
}
</style>
